import React, { useState, useEffect, useRef, Fragment } from "react";
import { MdClose, MdPlayArrow } from "react-icons/md";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";
import { Loader } from "@progress/kendo-react-indicators";
import { dataAPI } from "../../api/axios";
import { useSelector, useDispatch } from "react-redux";
import "./Search.css";
import { setResultsData } from "../../redux/resultsSlice";
import { setGeocodingData } from "../../redux/resultsSlice";
import { makeArray } from "../../permissionUtils";
import { sortSearchFilters, sortSearchQueryFilters } from "./searchUtils";
import { createResultsFromGeocode, ArcGISGeocoder } from "./geoCodeFuncs";
import { setActiveSideBarTab } from "../../redux/toolSelectSlice";

const Search = () => {
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const layerVisProperties = useSelector(
        (state) => state.configData.layerVisProperties
    );
    const fieldPermissions = useSelector(
        (state) => state.authorizedPermissions.fieldPermissions
    );
    const dispatch = useDispatch();

    // searchValue and searchValueRef hold the same data
    const [searchValue, setSearchValue] = useState(""); //str
    const searchValueRef = useRef(""); //str
    const [autocompleteResults, setAutocompleteResults] = useState([]); //str[]
    const [searchFilters, setSearchFilters] = useState();
    const [searchQueryFilters, setSearchQueryFilters] = useState([]);
    const [fetchingAutocompleteData, setFetchingAutocompleteData] = useState(false);
    const searchTimeoutRef = useRef();
    //popup
    const [visible, setVisible] = useState(false);
    const [filterVisible, setFilterVisible] = useState(false);
    const anchor = useRef(null);

    const autoCompleteTimeout = useRef();

    /* 
        Event handler functions
    */

    const handleInputFocus = () => {
        if (!visible && searchValueRef.current.length > 1) {
            setVisible(true);
            fetchAutocompleteData();
        }
    };

    const handleAutoCompleteClick = (value) => {
        fetchData(value);
        setVisible(false);
        setAutocompleteResults([]);
    };

    const handleClosePopup = (event) => {
        if (visible) {
            setVisible(false);
        }
        if (event.type === "click") {
            setSearchValue("");
            searchValueRef.current = "";
        }
    };

    const handleFilterPopup = () => {
        setFilterVisible(!filterVisible);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleClosePopup(event);
            setVisible(false);
            fetchData();
        }
    };

    const detectSearchClick = (e) => {
        const classes = e.target.classList;
        if (
            !classes.contains("search") &&
            !classes.contains("search-results") &&
            !classes.contains("search-result")
        ) {
            setVisible(false);
        }
    };

    const handleSearchTextChange = (e) => {
        searchValueRef.current = e.value;
        setSearchValue(e.value);
        setFilterVisible(false);
        setFetchingAutocompleteData(true);
        if (!searchValueRef.current) {
            //Search Textbox is empty
            window.clearTimeout(searchTimeoutRef.current);
            setVisible(false);
        }
        // Don't search if just one character
        else if (searchValueRef.current.length < 2) {
            setAutocompleteResults([]);
            setVisible(false);
        } else {
            autoCompleteDebounce();
            setVisible(true);
        }
    };

    /**
     *
     *      useEffects
     *
     */

    useEffect(() => {
        if (visible) {
            document.addEventListener("mousedown", detectSearchClick);
        }
        return () => {
            document.removeEventListener("mousedown", detectSearchClick);
        };
    }, [visible]);

    useEffect(() => {
        if (layerVisProperties && mapConfig) {
            let onSearches = [];
            mapConfig.SearchFields.forEach((element) => {
                const layer = layerVisProperties.find((i) => {
                    return i.Id === element.LayerID;
                });
                const permission = fieldPermissions?.find(
                    (f) =>
                        f?.layerId === element.LayerID && f?.fieldId === element.FieldName
                );
                // Check for read property and default to true if undefined
                const canRead = permission?.permissions?.Read ?? true;
                if (layer?.Visible && canRead) {
                    onSearches.push(element);
                }
            });

            let arr = sortSearchFilters(onSearches);
            setSearchFilters(arr);

            while (searchQueryFilters.length > 0) {
                searchQueryFilters.pop();
            }

            onSearches.forEach((sf) => {
                searchQueryFilters.push({
                    DisplayName: sf.DisplayName,
                    layerID: sf.LayerID,
                    fieldName: sf.FieldName,
                    FieldDisplayName: sf.FieldDisplayName,
                });
            });
            let arr2 = sortSearchQueryFilters(searchQueryFilters);
            setSearchQueryFilters(arr2);
        }
    }, [mapConfig, layerVisProperties]);

    /**
     *
     *      Search Field Functions
     *
     */

    /**
     *
     * @param {String} LayerID Id of layer
     * @param {String} FieldName Name of field
     * @param {Array<Object>} searchQueryFilters Fields to query
     * @returns
     */
    const isChecked = (LayerID, FieldName) => {
        let val = searchQueryFilters.filter(function (i) {
            if (i.layerID === LayerID) {
                if (i.fieldName === FieldName) {
                    return true;
                }
            }
            return false;
        });
        return val.length > 0;
    };

    /**
     *  Updates search fields to send to api
     * @param {ReactSyntheticEvent} event Checkbox toggle
     * @fires setSearchQueryFilters
     */
    const handleSearchFiltersChange = (event) => {
        const layerID = event.element.value.split("||")[0].trim();
        const fieldName = event.element.value.split("||")[1].trim();
        const DisplayName = event.element.value.split("||")[2].trim();
        let item = {
            layerID: layerID,
            fieldName: fieldName,
            DisplayName: DisplayName,
        };

        if (event.value) {
            //Add item to searchQueryFilter
            searchQueryFilters.push(item);
            let arr = sortSearchQueryFilters(searchQueryFilters);
            setSearchQueryFilters(arr);
        } else {
            //try to remove item to searchQueryFilter
            let filterList = searchQueryFilters.filter(function (i) {
                if (i.layerID === item.layerID) {
                    if (i.fieldName === item.fieldName) {
                        return false;
                    }
                }
                return true;
            });

            setSearchQueryFilters(filterList);
        }
    };

    /**
     *
     *      Data fetching functions
     *
     */

    /**
     *  POST DATAAPI Search/autocomplete
     * Triggered by event handler handleSearchTextChange & handleInputFocus
     *  @param {String} searchValueRef The value to search for
     *  @param {String} mapConfig.siteID Id of site
     *  @param {Array<Object>} searchQueryFilters Fields to query with search
     *  @fires setAutocompleteResults
     *  @fires setFetchingAutocompleteData
     */
    const fetchAutocompleteData = async () => {
        if (searchValueRef.current?.length < 1) return;
        setAutocompleteResults([]);
        setFetchingAutocompleteData(true);
        // Send Request
        const response = await dataAPI.post("Search/autocomplete", {
            siteID: mapConfig.siteID,
            value: searchValueRef.current,
            searchFields: searchQueryFilters,
        });
        // Request - Response matching
        const resonseQuery = JSON.parse(response?.config?.data)?.value;
        if (resonseQuery !== searchValueRef.current) {
            return;
        }
        // Set results data
        if (response?.data) {
            setAutocompleteResults(response?.data);
        } else {
            setAutocompleteResults([]);
        }
        setFetchingAutocompleteData(false);
    };

    /**
     * Function restrict autocomplete POST call to once every .25 seconds
     */
    const autoCompleteDebounce = () => {
        clearTimeout(autoCompleteTimeout.current);
        autoCompleteTimeout.current = setTimeout(() => fetchAutocompleteData(), 250);
    };

    /**
     *  POST DATAAPI Search
     * Search function to get data from dataAPI
     *  @param {String} searchValueRef The value to search for
     *  @param {String} mapConfig.siteID Id of site
     *  @param {Array<Object>} searchQueryFilters Fields query with search
     *  @param {String} value Value to search for
     *  @returns {[] | Array<Object>}
     */
    const fetchSiteSearchData = async (value) => {
        const response = await dataAPI.post("Search", {
            siteID: mapConfig.siteID,
            value: value ?? searchValueRef.current,
            searchFields: searchQueryFilters,
        });
        if (response?.data.length > 0) {
            let data = response.data;
            data.forEach((e) => {
                let layerConfig = mapConfig.SearchFields.filter(
                    (i) => i.LayerID === e.Id
                );
                if (layerConfig.length > 0) {
                    e.FeatureDescription = layerConfig[0].FeatureDescription;
                } else {
                    e.FeatureDescription = "";
                }
            });
            return data;
        } else {
            return [];
        }
    };

    /**
     *  GET SEARCH RESULTS FUNCTION
     * Main Search function. Triggered when autocomplete result is clicked OR when "enter" is hit
     * Gets GEOCODE data and DATAAPI data
     *  @param {String} value Value to search for
     *  @fires setResultsData
     *  @fires setActiveSideBarTab
     */
    const fetchData = async (value) => {
        const geocodeData = findAddress(mapConfig, value ?? searchValueRef.current);
        const siteSearchData = fetchSiteSearchData(value);
        // Get data from both API's synchronously
        const [geocodeRes, siteSearchRes] = await Promise.all([
            geocodeData,
            siteSearchData,
        ]);
        const finalResults = [];
        finalResults.push(...siteSearchRes);
        // Merge geocode results into final results
        geocodeRes.forEach((res) => {
            // Check to make sure Features are not empty
            if (res.Features.length > 0) {
                finalResults.push(res);
            }
        });
        // If there are results and they have features
        if (
            finalResults.length > 0 &&
            finalResults.some((res) => res.Features.length > 0)
        ) {
            dispatch(setResultsData(finalResults));
        }
        // No results / no results with features
        else {
            dispatch(setResultsData([]));
            dispatch(setActiveSideBarTab(2));
        }
    };

    /**
     *  GEOCODE HANDLER FUNCTION
     * This function serves 2 purposes:
     *   1. Return geocode data shaped as results
     *   2. Set geocode data to state so other components can add geocode points to map
     * @param {Object} mapconfig Map config from state
     * @param {String} searchValue String to search for
     * @fires setGeocodingData
     * @returns {Promise} Resolves to geocode data
     */
    async function findAddress(mapconfig, searchValue) {
        const mapData = mapconfig.map;
        if (!searchValue || searchValue.length < 0) {
            return;
        }
        if (!mapconfig.GeocodingEndpoints) {
            // Default no geocoder in config
            const data = await ArcGISGeocoder(null, mapData, searchValue);
            if (data.Features.length > 0) {
                dispatch(setGeocodingData(data));
                return createResultsFromGeocode(data);
            }
        } else {
            const endpoints = makeArray(mapconfig.GeocodingEndpoints.GeocodingEndpoint);
            const geocodeData = [];
            for (const i in endpoints) {
                if (endpoints[i]?.IncludeInGlobalSearch) {
                    if (
                        endpoints[i].Type.toLowerCase() === "ArcGISGeocoder".toLowerCase()
                    ) {
                        const data = await ArcGISGeocoder(
                            endpoints[i],
                            mapData,
                            searchValue
                        );
                        if (data.candidates.length > 0) {
                            dispatch(setGeocodingData(data));
                            geocodeData.push(...createResultsFromGeocode(data));
                        }
                    } else if (
                        endpoints[i].Type.toLowerCase() === "GoogleGeocoder".toLowerCase()
                    ) {
                    } else if (
                        endpoints[i].Type.toLowerCase() === "BingGeocoder".toLowerCase()
                    ) {
                    }
                }
            }
            return geocodeData;
        }
    }

    return (
        <Fragment>
            <div className="search-wrapper" ref={anchor} id="search-wrapper">
                <Input
                    className="search"
                    value={searchValue}
                    onChange={handleSearchTextChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Search..."
                    onFocus={handleInputFocus}
                    style={{ marginRight: "0.5rem" }}
                />
                {searchValue && (
                    <Button
                        className="close"
                        fillMode={null}
                        rounded={"full"}
                        onClick={(e) => handleClosePopup(e)}>
                        <MdClose />
                    </Button>
                )}
                <Button
                    className="search-filter"
                    fillMode={null}
                    onClick={handleFilterPopup}>
                    <MdPlayArrow style={{ transform: "rotate(90deg)" }} />
                </Button>
            </div>
            {/* Popup is shown when searchValueRef.current.length > 1 */}
            <Popup
                anchor={anchor.current}
                show={visible}
                popupClass={"search-results"}
                animate={false}>
                {fetchingAutocompleteData === false ? (
                    // if there are results, show them
                    autocompleteResults?.length > 0 ? (
                        <div key={"search_divResults"}>
                            {autocompleteResults.map(function (d, idx) {
                                return (
                                    <div
                                        className="search-result"
                                        onClick={() => handleAutoCompleteClick(d.Value)}
                                        key={`${idx}${d.Value}`}>
                                        {d.Value}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // If autocompleteResults is empty array (no results)
                        <h4 className="no-features">
                            <span
                                className="k-icon k-i-warning"
                                style={{
                                    fontSize: "1.5em",
                                }}
                            />
                            No features found
                        </h4>
                    )
                ) : (
                    // if autocompleteResults or searchValueRef are undefined
                    <div key={"search_Results_loader"} className="loader">
                        <Loader type="infinite-spinner" />
                    </div>
                )}
            </Popup>
            <Popup
                anchor={anchor.current}
                show={filterVisible}
                popupClass={"search-results"}
                animate={false}>
                {searchFilters && searchFilters.length > 0 ? (
                    <ul>
                        {searchFilters.map(function (d, idx) {
                            return (
                                <li key={"li_" + idx} id={"li_" + idx}>
                                    <Checkbox
                                        id={"ckb_" + idx}
                                        defaultChecked={isChecked(d.LayerID, d.FieldName)}
                                        label={d.DisplayName + " - " + d.FieldDisplayName}
                                        value={
                                            d.LayerID +
                                            "||" +
                                            d.FieldName +
                                            "||" +
                                            d.DisplayName
                                        }
                                        onChange={(e) => {
                                            handleSearchFiltersChange(e.target);
                                        }}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="loader">
                        <Loader type="infinite-spinner" />
                    </div>
                )}
            </Popup>
        </Fragment>
    );
};

export default Search;
